import { useCallback, useEffect, useMemo, useState } from 'react';
import getMobileOperatingSystem from '@picsart/rc/getMobileOperatingSystem';
import { useTranslator } from '@picsart/rc/services/localization';

import {
  DEFAULT_PLUS_TIER_SCOPE_ID,
  DEFAULT_PRO_TIER_SCOPE_ID,
  DEFAULT_SCOPE_ID,
  DEFAULT_TEAM_SCOPE_ID,
} from '@picsart/monetization/constants';
import {
  SubscriptionPlan,
  SubscriptionPlanFrequencyType,
} from '@picsart/monetization/types/models/subscriptionPlanModel';
import { fetchSubscriptionPlans } from '@picsart/monetization/subscriptionsService';
import { fetchFeaturesAvailability } from '@picsart/monetization/featuresService';
import Switch from '@picsart/monetization/Switch';

import { PricingCardProps } from 'components/PricingCards/PricingCard/types';
import PricingCardsSkeleton from './PricingCardsSkeleton';
import { getCmsCards, getGoldCards, getTierCards } from './data';
import PricingCard from './PricingCard';
import useStyles from './styles';

export default function PricingCards({
  pricingCards,
  scopeId,
  pricingCardWidth,
  pricingCardHeight,
}: {
  pricingCards?: PricingCardProps[];
  scopeId?: string;
  pricingCardWidth?: string;
  pricingCardHeight?: string;
}) {
  const [frequency, setFrequency] = useState<SubscriptionPlanFrequencyType>('monthly');
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [areTiersAvailable, setAreTiersAvailable] = useState<boolean>();
  const isMobile = !!getMobileOperatingSystem(null);
  const translate = useTranslator();
  const classes = useStyles();

  useEffect(() => {
    void Promise.all([
      fetchFeaturesAvailability(['tier']),
      fetchSubscriptionPlans(
        scopeId
          ? [scopeId]
          : [
              DEFAULT_SCOPE_ID,
              DEFAULT_TEAM_SCOPE_ID,
              DEFAULT_PLUS_TIER_SCOPE_ID,
              DEFAULT_PRO_TIER_SCOPE_ID,
            ],
      ),
    ]).then(([featuresResponse, subscriptionPlansResponse]) => {
      if (featuresResponse.status === 'success') {
        setAreTiersAvailable(featuresResponse.response.tier?.is_available);
      }
      if (subscriptionPlansResponse.status === 'success') {
        setSubscriptionPlans(subscriptionPlansResponse.data || []);
      }
    });
  }, [scopeId]);

  const cards = useMemo(() => {
    if (areTiersAvailable === undefined || !subscriptionPlans.length || !scopeId) {
      return [];
    }

    if (pricingCards?.length && scopeId) {
      return getCmsCards(subscriptionPlans, frequency, pricingCards, isMobile);
    }

    if (areTiersAvailable) {
      return getTierCards(subscriptionPlans, frequency, isMobile);
    }

    return getGoldCards(subscriptionPlans, frequency, isMobile);
  }, [areTiersAvailable, subscriptionPlans, pricingCards, scopeId, frequency, isMobile]);

  const handleFrequencyChange = useCallback((event: boolean) => {
    setFrequency(event ? 'yearly' : 'monthly');
  }, []);

  if (!cards.length) {
    return <PricingCardsSkeleton itemsCount={pricingCards?.length || 3} />;
  }

  return (
    <div data-pulse-section-group="Pricing Section">
      <div data-pulse-section="Year/Month Toggle">
        <Switch
          suffixLabel={translate('Yearly (Best value)')}
          prefixLabel={translate('Monthly')}
          onChange={handleFrequencyChange}
          checked={frequency === 'yearly'}
          className={classes.switch}
        />
      </div>
      <div className={classes.container}>
        {cards.map((data, index) => (
          <PricingCard
            pricingCardWidth={pricingCardWidth}
            pricingCardHeight={pricingCardHeight}
            data-pulse-section={data.cardId}
            className={classes.card}
            data-testid={data.cardId}
            key={`${data.id}-${data.cardId}-${index}`}
            {...data}
          />
        ))}
      </div>
    </div>
  );
}
